/**
 * Change Password Page
 * Allow users to change their own password
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { AdminUserService } from '../../services/adminUser/AdminUserService';
import { useMessage } from '../../contexts/MessageContext';
import { Card, CardContent, TextField, PrimaryButton, SecondaryButton } from '../../components/ui';

const calculatePasswordStrength = (password: string): { strength: 'weak' | 'medium' | 'strong'; value: number } => {
  if (!password) {
    return { strength: 'weak', value: 0 };
  }

  let strength = 0;

  // Length check
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 10;

  // Character variety checks
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 15;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 15;

  // Bonus for longer passwords
  if (password.length >= 16) strength += 10;

  if (strength < 40) {
    return { strength: 'weak', value: strength };
  } else if (strength < 70) {
    return { strength: 'medium', value: strength };
  } else {
    return { strength: 'strong', value: strength };
  }
};

const getPasswordStrengthColor = (strength: 'weak' | 'medium' | 'strong', theme: Theme): string => {
  switch (strength) {
    case 'weak':
      return theme.palette.error.main;
    case 'medium':
      return theme.palette.warning.main;
    case 'strong':
      return theme.palette.success.main;
    default:
      return theme.palette.text.secondary;
  }
};

const ChangePasswordPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showError, showSuccess } = useMessage();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const passwordStrength = calculatePasswordStrength(newPassword);

  // Check if all required fields are filled
  const isFormValid = (): boolean => {
    const trimmedCurrentPassword = currentPassword.trim();
    const trimmedNewPassword = newPassword.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    return !!(
      trimmedCurrentPassword &&
      trimmedNewPassword &&
      trimmedNewPassword.length >= 8 &&
      trimmedConfirmPassword &&
      trimmedNewPassword !== trimmedCurrentPassword
    );
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    const trimmedCurrentPassword = currentPassword.trim();
    const trimmedNewPassword = newPassword.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (!trimmedCurrentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!trimmedNewPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (trimmedNewPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (trimmedNewPassword === trimmedCurrentPassword) {
      newErrors.newPassword = 'New password cannot be the same as current password';
    }

    if (!trimmedConfirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (trimmedNewPassword !== trimmedConfirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await AdminUserService.changePassword({
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
      });

      showSuccess('Password changed successfully!');

      // Important: Only navigate if the above call truly succeeded without throwing
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Failed to change password:', error);
      // Instead of popup, set the inline error
      setSubmitError(error.message || 'Failed to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <MainLayout>


      {/* Page Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          marginBottom: theme.spacing(3),
        }}
      >
        Change Password
      </Typography>

      {/* Form Card */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(3),
              fontSize: theme.typography.body1.fontSize,
            }}
          >
            Update Your Password
          </Typography>

          <Grid container spacing={3}>
            {/* Current Password */}
            <Grid item xs={12}>
              <TextField
                label="Current Password"
                required
                placeholder="Enter your current password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (errors.currentPassword) {
                    setErrors({ ...errors, currentPassword: undefined });
                  }
                  if (submitError) {
                    setSubmitError(null);
                  }
                }}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* New Password */}
            <Grid item xs={12}>
              <TextField
                label="New Password"
                required
                placeholder="Enter your new password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errors.newPassword) {
                    setErrors({ ...errors, newPassword: undefined });
                  }
                }}
                error={!!errors.newPassword || (!!newPassword && !!currentPassword && newPassword === currentPassword)}
                helperText={errors.newPassword || (newPassword && currentPassword && newPassword === currentPassword ? 'New password cannot be the same as current password' : 'Must be at least 8 characters long.')}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Confirm New Password */}
            <Grid item xs={12}>
              <TextField
                label="Confirm New Password"
                required
                placeholder="Re-enter your new password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) {
                    setErrors({ ...errors, confirmPassword: undefined });
                  }
                }}
                error={!!errors.confirmPassword || (!!confirmPassword && newPassword !== confirmPassword)}
                helperText={errors.confirmPassword || (confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : '')}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Password Strength */}
            {newPassword && (
              <Grid item xs={12}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.caption.fontSize,
                      }}
                    >
                      Password Strength
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: getPasswordStrengthColor(passwordStrength.strength, theme),
                        fontSize: theme.typography.caption.fontSize,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    >
                      {passwordStrength.strength}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.value}
                    sx={{
                      height: 6,
                      borderRadius: theme.shapes.borderRadius.md,
                      backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getPasswordStrengthColor(passwordStrength.strength, theme),
                      },
                    }}
                  />
                </Box>
              </Grid>
            )}

            {/* API Submit Error */}
            {submitError && (
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    display: 'block',
                    marginTop: theme.spacing(1),
                  }}
                >
                  {submitError}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.spacing(2),
              marginTop: theme.spacing(4),
            }}
          >
            <SecondaryButton onClick={handleCancel}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting ? 'Changing...' : 'Change Password'}
            </PrimaryButton>
          </Box>
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default ChangePasswordPage;
